import React, { useEffect, useState } from 'react';
import { X, Search, MapPin, Navigation } from 'lucide-react';

export default function LocationModal({ isOpen, onClose }) {
  const [districts, setDistricts] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [locationError, setLocationError] = useState(false);
  const [selectedDistId, setSelectedDistId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      const storedDistId = localStorage.getItem('selected_dist_id');
      setSelectedDistId(storedDistId ? storedDistId.toString() : null);

      setLoading(true);
      fetch('/api/districts', {
        headers: {
          'Client-ID': 'lokanatya_client',
          'Client-Secret': 'super_secret_key_123'
        }
      })
        .then(res => res.json())
        .then(data => {
          if (data.status === 'success' && data.data) {
            const list = Array.isArray(data.data) ? data.data : (typeof data.data === 'object' ? Object.values(data.data) : []);
            setDistricts(list);
          } else {
            setDistricts([]);
          }
        })
        .catch(err => {
          console.error('Districts API Error:', err);
          setDistricts([]);
        })
        .finally(() => setLoading(false));
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSelect = (dist_id, district_name) => {
    if (dist_id === null) {
      localStorage.removeItem('selected_dist_id');
      localStorage.setItem('selected_district_name', district_name);
    } else {
      localStorage.setItem('selected_dist_id', dist_id);
      localStorage.setItem('selected_district_name', district_name);
    }
    window.dispatchEvent(new Event('locationChanged'));
    onClose();
  };

  const handleDetectLocation = () => {
    setLocationError(true);
  };

  const safeDistricts = Array.isArray(districts) ? districts : [];

  const filteredDistricts = safeDistricts.filter(d => 
    d && d.district_name && String(d.district_name).toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div 
      className="fixed inset-0 z-[100] flex flex-col justify-end md:items-center md:justify-center bg-black/60 backdrop-blur-sm transition-opacity" 
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white w-full md:max-w-2xl h-[80vh] md:h-auto md:max-h-[80vh] rounded-t-[2rem] md:rounded-3xl flex flex-col relative animate-slide-up shadow-2xl overflow-hidden">
        
        {/* Drag Handle for Mobile */}
        <div className="w-full flex justify-center pt-3 pb-1 md:hidden">
          <div className="w-12 h-1.5 bg-gray-200 rounded-full"></div>
        </div>

        {/* Header */}
        <div className="flex justify-between items-center px-5 pt-3 pb-3 md:px-6 md:pt-5 md:pb-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg md:text-xl font-bold text-gray-900 tracking-tight">Select Location</h2>
            <p className="text-xs text-gray-500 font-medium mt-0.5">Discover shows playing near you</p>
          </div>
          <button onClick={onClose} className="bg-gray-100 hover:bg-gray-200 p-2 rounded-full text-gray-600 transition-colors">
            <X size={18} className="stroke-[2.5]" />
          </button>
        </div>

        {/* Search & Auto-detect */}
        <div className="p-4 md:p-5 border-b border-gray-50 bg-gray-50/30">
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
              <Search className="text-gray-400 group-focus-within:text-red-500 transition-colors" size={16} />
            </div>
            <input
              type="text"
              placeholder="Search for your district..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`block w-full pl-10 pr-4 py-2.5 border ${locationError ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-red-500 hover:border-gray-300'} rounded-xl bg-white focus:bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-100 text-xs sm:text-sm font-medium transition-all shadow-2xs`}
            />
          </div>
          
          <button onClick={handleDetectLocation} className="mt-3 flex items-center justify-center w-full md:w-auto text-red-600 bg-red-50 hover:bg-red-100 px-3.5 py-2 rounded-lg text-xs font-bold transition-colors border border-red-100">
            <Navigation size={14} className="mr-1.5" />
            Detect my current location
          </button>
          
          {locationError && (
            <p className="text-red-500 text-[11px] font-medium mt-2 flex items-center gap-1"><X size={13} /> Could not detect location. Please select manually.</p>
          )}
        </div>

        {/* Districts Grid / List */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-3 border-red-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <>
              {/* Special 'All Odisha' Option */}
              <div>
                <button
                  onClick={() => handleSelect(null, 'Across Odisha')}
                  className={`w-full flex items-center justify-between p-3 rounded-xl border text-left transition-all ${
                    selectedDistId === null
                      ? 'border-red-500 bg-red-50/50 text-red-600 font-bold shadow-2xs'
                      : 'border-gray-100 hover:border-red-200 hover:bg-gray-50 text-gray-800 font-semibold'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-red-100 text-red-600 flex items-center justify-center shrink-0">
                      <MapPin size={16} />
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-bold">Across Odisha (All Locations)</h4>
                      <p className="text-[10px] text-gray-500 font-medium">Show events from all districts</p>
                    </div>
                  </div>
                  {selectedDistId === null && (
                    <span className="w-2 h-2 rounded-full bg-red-500"></span>
                  )}
                </button>
              </div>

              {/* Districts List */}
              <div>
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2.5">Popular Districts</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {filteredDistricts.map((d, index) => {
                    const distIdStr = d.dist_id ? d.dist_id.toString() : index.toString();
                    const isSelected = selectedDistId === distIdStr;
                    return (
                      <button
                        key={d.dist_id || index}
                        onClick={() => handleSelect(d.dist_id, d.district_name)}
                        className={`flex items-center gap-2 p-2.5 rounded-xl border text-left transition-all ${
                          isSelected
                            ? 'border-red-500 bg-red-50/60 text-red-600 font-bold shadow-2xs'
                            : 'border-gray-100 hover:border-red-200 hover:bg-gray-50/80 text-gray-700 font-medium'
                        }`}
                      >
                        <MapPin size={14} className={isSelected ? 'text-red-500' : 'text-gray-400'} />
                        <span className="text-xs truncate">{d.district_name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

      </div>
    </div>
  );
}
